import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { HiOutlineFilter, HiOutlineSearch } from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';
import './ColumnFilter.css';

// Server-side sentinel for null / missing / empty values.
export const BLANK_VALUE = '__BLANK__';

const PANEL_WIDTH = 260;

// The list scrolls, but the search box and the Apply/Cancel row do not: the
// panel is never shorter than this, so placement has to reserve it.
const PANEL_CHROME = 110;
const LIST_MAX = 260;
const LIST_MIN = 120;
const MARGIN = 8;
const GAP = 6;

/**
 * Excel-style column filter: a funnel button in a table header that opens a
 * searchable checkbox list of every distinct value in that column.
 *
 * Two value sources, and a caller supplies exactly one:
 *
 * - `options` — a ready list of { value, count }. Nothing is fetched. For a
 *   caller that already holds every row, such as a report the server returns
 *   whole; a second source of truth for "which values exist" would only be a
 *   way for the two to disagree.
 * - `endpoint` — a URL queried with `column` + `queryParams`. For a caller
 *   whose rows live on the server. Customer Register uses this: its endpoint
 *   honours the tenant scope, the search box and every *other* column's
 *   filter, so options narrow as you filter while the column being edited
 *   always shows its full list (same as Excel).
 *
 * There is deliberately no default endpoint — a shared component defaulting to
 * one module's URL is how the next caller silently queries the wrong data.
 *
 * `selected` is the applied filter: an empty array means "no filter / all
 * values". Edits are held in local draft state until Apply is pressed.
 */
const ColumnFilter = ({
    column, label, selected = [], onChange,
    options: providedOptions, endpoint, queryParams = {},
}) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [truncated, setTruncated] = useState(false);
    const [search, setSearch] = useState('');
    const [draft, setDraft] = useState([]);
    const [position, setPosition] = useState({
        top: 0, left: 0, bottom: undefined, flip: false, listMaxHeight: LIST_MAX
    });

    const buttonRef = useRef(null);
    const panelRef = useRef(null);

    const isActive = selected.length > 0;

    // Serialised so the effect below re-fetches when the surrounding filters move.
    const queryKey = JSON.stringify(queryParams);

    const fetchOptions = useCallback(async () => {
        if (!endpoint) return;
        try {
            setLoading(true);
            const response = await axiosInstance.get(endpoint, {
                params: { column, ...queryParams }
            });
            if (response.data.success) {
                setOptions(response.data.data || []);
                setTruncated(!!response.data.truncated);
            }
        } catch (error) {
            console.error(`Error fetching filter options for ${column}:`, error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [column, endpoint, queryKey]);

    useEffect(() => {
        if (!open) return;
        if (providedOptions) {
            setOptions(providedOptions);
            setTruncated(false);
            setLoading(false);
            return;
        }
        fetchOptions();
    }, [open, providedOptions, fetchOptions]);

    // Anchor the panel to the funnel button. Fixed positioning + a portal keep
    // it out of the table's overflow containers, which would otherwise clip it.
    //
    // Below the button when it fits, above it when it does not: a filtered-down
    // table is short, so its header can sit near the bottom of the window, and a
    // panel opening downwards there put Apply/Cancel off-screen with no way to
    // scroll to them (the panel is fixed). Whichever side wins, the list is
    // capped to the room actually there, so that row is always reachable.
    const placePanel = useCallback(() => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const left = Math.max(
            MARGIN,
            Math.min(rect.left, window.innerWidth - PANEL_WIDTH - MARGIN)
        );

        const below = window.innerHeight - rect.bottom - GAP - MARGIN;
        const above = rect.top - GAP - MARGIN;
        const flip = below < LIST_MIN + PANEL_CHROME && above > below;
        const space = flip ? above : below;

        setPosition({
            left,
            flip,
            top: flip ? undefined : rect.bottom + GAP,
            bottom: flip ? window.innerHeight - rect.top + GAP : undefined,
            listMaxHeight: Math.max(LIST_MIN, Math.min(LIST_MAX, space - PANEL_CHROME))
        });
    }, []);

    const handleToggle = () => {
        if (open) {
            setOpen(false);
            return;
        }
        placePanel();
        setDraft(selected);
        setSearch('');
        setOpen(true);
    };

    // Close on outside click / Escape, and follow the button on scroll or resize.
    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (event) => {
            if (panelRef.current?.contains(event.target)) return;
            if (buttonRef.current?.contains(event.target)) return;
            setOpen(false);
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', placePanel);
        window.addEventListener('scroll', placePanel, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', placePanel);
            window.removeEventListener('scroll', placePanel, true);
        };
    }, [open, placePanel]);

    const displayLabel = (value) => (value === BLANK_VALUE ? '(Blanks)' : value);

    const visibleOptions = options.filter((opt) => {
        if (!search.trim()) return true;
        return displayLabel(opt.value).toLowerCase().includes(search.toLowerCase().trim());
    });

    // "(Select All)" acts on the currently visible (searched) rows, like Excel.
    const visibleValues = visibleOptions.map(o => o.value);
    const allVisibleChecked = visibleValues.length > 0 && visibleValues.every(v => draft.includes(v));
    const someVisibleChecked = visibleValues.some(v => draft.includes(v));

    const toggleValue = (value) => {
        setDraft(prev => (
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        ));
    };

    const toggleSelectAll = () => {
        if (allVisibleChecked) {
            setDraft(prev => prev.filter(v => !visibleValues.includes(v)));
        } else {
            setDraft(prev => [...new Set([...prev, ...visibleValues])]);
        }
    };

    const handleApply = () => {
        // Everything ticked is the same as no filter — keep the URL clean.
        const allValues = options.map(o => o.value);
        const isAll = allValues.length > 0 && allValues.every(v => draft.includes(v));
        onChange(isAll ? [] : draft);
        setOpen(false);
    };

    const handleClear = () => {
        onChange([]);
        setOpen(false);
    };

    return (
        <span className="col-filter">
            <button
                type="button"
                ref={buttonRef}
                className={`col-filter-btn ${isActive ? 'active' : ''}`}
                onClick={handleToggle}
                title={isActive ? `${label}: ${selected.length} selected` : `Filter by ${label}`}
                aria-label={`Filter by ${label}`}
                aria-expanded={open}
            >
                <HiOutlineFilter size={14} />
                {isActive && <span className="col-filter-badge">{selected.length}</span>}
            </button>

            {open && createPortal(
                <div
                    className="col-filter-panel"
                    ref={panelRef}
                    style={{
                        top: position.top,
                        bottom: position.bottom,
                        left: position.left,
                        width: PANEL_WIDTH
                    }}
                >
                    <div className="col-filter-panel-search">
                        <HiOutlineSearch size={16} />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={`Search ${label.toLowerCase()}...`}
                            autoFocus
                        />
                    </div>

                    <div
                        className="col-filter-panel-list"
                        style={{ maxHeight: position.listMaxHeight }}
                    >
                        {loading ? (
                            <div className="col-filter-panel-msg">Loading...</div>
                        ) : visibleOptions.length === 0 ? (
                            <div className="col-filter-panel-msg">No values found</div>
                        ) : (
                            <>
                                <label className="col-filter-option col-filter-select-all">
                                    <input
                                        type="checkbox"
                                        checked={allVisibleChecked}
                                        ref={(el) => {
                                            if (el) el.indeterminate = !allVisibleChecked && someVisibleChecked;
                                        }}
                                        onChange={toggleSelectAll}
                                    />
                                    <span className="col-filter-option-text">(Select All)</span>
                                </label>
                                {visibleOptions.map((opt) => (
                                    <label key={opt.value} className="col-filter-option">
                                        <input
                                            type="checkbox"
                                            checked={draft.includes(opt.value)}
                                            onChange={() => toggleValue(opt.value)}
                                        />
                                        <span
                                            className={`col-filter-option-text ${opt.value === BLANK_VALUE ? 'blank' : ''}`}
                                            title={displayLabel(opt.value)}
                                        >
                                            {displayLabel(opt.value)}
                                        </span>
                                        <span className="col-filter-option-count">{opt.count}</span>
                                    </label>
                                ))}
                            </>
                        )}
                        {truncated && (
                            <div className="col-filter-panel-msg">
                                Showing the first {options.length} values — use search to narrow down.
                            </div>
                        )}
                    </div>

                    <div className="col-filter-panel-actions">
                        <button type="button" className="col-filter-link" onClick={handleClear}>
                            Clear filter
                        </button>
                        <div className="col-filter-panel-buttons">
                            <button type="button" className="col-filter-cancel" onClick={() => setOpen(false)}>
                                Cancel
                            </button>
                            <button type="button" className="col-filter-apply" onClick={handleApply}>
                                OK
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </span>
    );
};

export default ColumnFilter;
